import { Vec2 } from '../utils/math';
import { Component } from '../engine/Component';
import { TracedSegment } from '../engine/Ray';
import { Quadtree } from '../spatial/Quadtree';

export interface SnapTarget {
  type: 'beam_segment' | 'focal_point';
  segment?: TracedSegment;
  point?: Vec2;
  direction?: Vec2;
}

export interface SnapSolution {
  position: Vec2;
  rotation: number;
  distance: number;
  priority: number;
  hint: string;
  target: SnapTarget;
}

/**
 * Constraint solver for snap-to placement of optical components.
 * Finds the best snap position based on proximity to beam segments,
 * focal points, and optimal angles (perpendicular, Brewster).
 */
export class ConstraintSolver {
  private quadtree: Quadtree;
  private snapTolerance: number;

  constructor(worldWidth?: number, worldHeight?: number, snapTolerance: number = 25) {
    worldWidth ??= window.innerWidth;
    worldHeight ??= window.innerHeight;
    this.quadtree = new Quadtree({ x: 0, y: 0, width: worldWidth, height: worldHeight });
    this.snapTolerance = snapTolerance;
  }

  /** Rebuild the quadtree from current traced segments.
   *  @param excludeSourceId If set, segments from this laser source are excluded (to prevent self-snap). */
  indexSegments(segments: TracedSegment[], excludeSourceId?: number): void {
    this.quadtree.clear();

    for (const seg of segments) {
      if (excludeSourceId !== undefined && seg.sourceId === excludeSourceId) continue;

      const minX = Math.min(seg.start.x, seg.end.x);
      const minY = Math.min(seg.start.y, seg.end.y);
      const maxX = Math.max(seg.start.x, seg.end.x);
      const maxY = Math.max(seg.start.y, seg.end.y);
      const padding = 2;

      this.quadtree.insert({
        x: minX - padding,
        y: minY - padding,
        width: (maxX - minX) + padding * 2,
        height: (maxY - minY) + padding * 2,
        data: { type: 'beam_segment', segment: seg } as SnapTarget,
      });
    }
  }

  /** Find the best snap position for a component being dragged */
  findBestSnap(mousePos: Vec2, component: Component): SnapSolution | null {
    const candidates = this.quadtree.queryRadius(mousePos, this.snapTolerance);
    const solutions: SnapSolution[] = [];

    for (const candidate of candidates) {
      const target = candidate.data as SnapTarget;

      if (target.type === 'beam_segment' && target.segment) {
        const beamSnap = this.solveBeamIntersection(mousePos, component, target.segment);
        if (beamSnap) solutions.push(beamSnap);

        const brewsterSnap = this.solveBrewsterAngle(mousePos, component, target.segment);
        if (brewsterSnap) solutions.push(brewsterSnap);
      }
    }

    if (solutions.length === 0) return null;

    // Return closest solution, weighted by priority
    solutions.sort((a, b) => (a.distance / a.priority) - (b.distance / b.priority));
    return solutions[0];
  }

  /**
   * Beam intersection constraint: snap component to the closest point on a beam,
   * oriented perpendicular to the beam direction.
   */
  private solveBeamIntersection(mousePos: Vec2, _component: Component, segment: TracedSegment): SnapSolution | null {
    const segDir = segment.end.sub(segment.start);
    const segLen = segDir.length();
    if (segLen < 1e-6) return null;
    const segNorm = segDir.scale(1 / segLen);

    // Project mouse position onto segment
    const toMouse = mousePos.sub(segment.start);
    let t = toMouse.dot(segNorm);
    t = Math.max(0, Math.min(segLen, t)); // clamp to segment

    const closestPoint = segment.start.add(segNorm.scale(t));
    const distance = mousePos.distanceTo(closestPoint);

    if (distance > this.snapTolerance) return null;

    // Perpendicular orientation: surface normal aligned with beam direction
    const beamAngle = Math.atan2(segDir.y, segDir.x);
    const perpendicularAngle = beamAngle; // mirror surface along beam = perpendicular reflection

    return {
      position: closestPoint,
      rotation: perpendicularAngle,
      distance,
      priority: 1.0,
      hint: 'beam_snap',
      target: { type: 'beam_segment', segment, direction: segNorm },
    };
  }

  /**
   * Brewster angle constraint: for glass/prism components,
   * orient at Brewster's angle for minimum reflection.
   */
  private solveBrewsterAngle(mousePos: Vec2, component: Component, segment: TracedSegment): SnapSolution | null {
    if (component.type !== 'glass' && component.type !== 'prism') return null;

    const segDir = segment.end.sub(segment.start).normalize();
    const closestPoint = this.closestPointOnSegment(mousePos, segment);
    const distance = mousePos.distanceTo(closestPoint);

    if (distance > this.snapTolerance) return null;

    // Brewster angle: θ_B = arctan(n2/n1)
    const n2 = 1.52; // typical glass
    const brewsterAngle = Math.atan(n2);
    const beamAngle = Math.atan2(segDir.y, segDir.x);

    // Surface normal should make Brewster angle with beam
    const surfaceAngle = beamAngle + Math.PI / 2 - brewsterAngle;

    return {
      position: closestPoint,
      rotation: surfaceAngle,
      distance,
      priority: 0.6, // lower priority than perpendicular snap
      hint: 'brewster_angle',
      target: { type: 'beam_segment', segment, direction: segDir },
    };
  }

  private closestPointOnSegment(point: Vec2, segment: TracedSegment): Vec2 {
    const segDir = segment.end.sub(segment.start);
    const segLen = segDir.length();
    if (segLen < 1e-6) return segment.start;
    const segNorm = segDir.scale(1 / segLen);
    const t = Math.max(0, Math.min(segLen, point.sub(segment.start).dot(segNorm)));
    return segment.start.add(segNorm.scale(t));
  }
}
